import { ref, reactive, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getShopList } from "@/common/graphql/shop";
import {
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import {
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ListDeliveryAreaDocument,
  ShopListInput,
  DeliveryTimeListInput,
  DeliveryTimeAllListInput,
  DeliveryTimeCommonInput,
  DeliveryTimeDeleteInput,
  DeliveryTimeGetInput,
  DeliveryGroupListInput,
  DeliveryGroupGetInput,
  ListDeliveryGroupQuery,
  ListDeliveryGroupQueryVariables,
  ListDeliveryGroupDocument,
} from "@/generated/graphql";
import type {
  ChainListItem,
  ShopListItem,
  AreaListItem,
  DeliveryGroupListItem,
  ChainSelectValue,
  ShopSelectValue,
  AreaSelectValue,
  DeliveryGroupSelectValue,
  ListData,
  ListGroupDeliveryId,
  ListDeliveryTimeItem,
} from "../types";

interface UseDeliverySettingFilterParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  displayInputDialog: Ref<boolean>;
  displayConfirmDialog: Ref<boolean>;
  selectShopFlg: Ref<boolean>;
  selectAreaFlg: Ref<boolean>;
  mode: Ref<string>;
  model: Ref<number>;
  queryDeliverytime: () => Promise<void>;
  onChangeGroup: () => Promise<void>;
  filterState?: ReturnType<typeof useDeliverySettingFilterState>;
}

/**
 * 画面で共有する状態を初期化して返却する composable。
 *
 * @returns 画面で利用するリアクティブな状態オブジェクト
 */
export const useDeliverySettingFilterState = () => {
  // チェーンリスト
  const chainListItems = ref<ChainListItem[]>([]);

  // チェーン選択値
  const chainSelectValues = ref<ChainSelectValue>({
    chain_code: "",
    name: "",
  });

  // 店舗リスト
  const shopListItems = ref<ShopListItem[]>([]);

  // 店舗選択値
  const shopSelectValues = ref<ShopSelectValue>({
    shop_code: "",
    name: "",
  });

  // エリアリスト
  const areaListItems = ref<AreaListItem[]>([]);

  // 新規登録用エリアリスト
  const newAreaListItems = ref<typeof areaListItems.value>([]);

  // エリア選択値
  const areaSelectValues = ref<AreaSelectValue>({
    area_code: "",
    area_name: "",
  });
  const areaEditSelectValues = ref<AreaSelectValue>({
    area_code: "",
    area_name: "",
  });

  // 配達グループリスト
  const deliveryGroupListItems = ref<DeliveryGroupListItem[]>([]);

  // 配達グループ選択値
  const deliveryGroupSelectValues = ref<DeliveryGroupSelectValue>({
    group_code: "",
    name: "",
  });
  const bufDeliveryGroupSelectValues = ref<DeliveryGroupSelectValue>({
    group_code: "",
    name: "",
  });

  /**
   * 店舗一覧 input
   */
  const shopListInput = reactive<ShopListInput>({
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    limit: 10000,
    offset: 0,
  });

  /**
   * 配達時刻一覧 input
   */
  const deliveryTimeListInput = reactive<DeliveryTimeListInput>({
    area_code: [],
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    shop_code: "",
    limit: 10000,
    offset: 0,
  });

  /**
   * 配達時刻一覧ALL input
   */
  const deliveryTimeAllListInput = reactive<DeliveryTimeAllListInput>({
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    shop_code: "",
    limit: 50,
    offset: 0,
  });

  /**
   * 配達時間登録 input
   */
  const deliveryTimeCommonInput = reactive<DeliveryTimeCommonInput>({
    area_code: "",
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    delivery_end_time: "",
    delivery_id: 0,
    delivery_start_time: "",
    max_deliverable_unit: null,
    shop_code: "",
    user_id: localStorageGetUserId(),
  });

  /**
   * 配達時間削除 input
   */
  const deliveryTimeDeleteInput = reactive<DeliveryTimeDeleteInput>({
    area_code: "",
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    delivery_id: 0,
    shop_code: "",
    user_id: localStorageGetUserId(),
  });

  /**
   * 配達時間存在チェック input
   */
  const deliveryTimeGetInput = reactive<DeliveryTimeGetInput>({
    customer_code: localStorageGetCustomerCode(),
    chain_code: "",
    shop_code: "",
    area_code: "",
    delivery_id: 0,
  });

  /**
   * 配達グループ一覧 input
   */
  const deliveryGroupListInput = reactive<DeliveryGroupListInput>({
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
  });

  /**
   * グループ便一覧 input
   */
  const deliveryGroupGetInput = reactive<DeliveryGroupGetInput>({
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    group_code: "",
  });

  // 一覧データ配列
  const listDataArray = ref<ListData[]>([]);
  const listGroupDeliveryIdDataArray = ref<ListGroupDeliveryId[]>([]);

  // マップ
  const mappedListData = ref<Map<string, ListData[]>>(new Map());

  // 配達時間レスポンス
  const listDeliveryTime = ref<ListDeliveryTimeItem[]>([]);

  return {
    chainListItems,
    chainSelectValues,
    shopListItems,
    shopSelectValues,
    areaListItems,
    newAreaListItems,
    areaSelectValues,
    areaEditSelectValues,
    deliveryGroupListItems,
    deliveryGroupSelectValues,
    bufDeliveryGroupSelectValues,
    shopListInput,
    deliveryTimeListInput,
    deliveryTimeAllListInput,
    deliveryTimeCommonInput,
    deliveryTimeDeleteInput,
    deliveryTimeGetInput,
    deliveryGroupListInput,
    deliveryGroupGetInput,
    listDataArray,
    listGroupDeliveryIdDataArray,
    mappedListData,
    listDeliveryTime,
  };
};

/**
 * 絞り込み条件の状態管理と適用処理を提供する composable。
 *
 * @returns 画面フォームで利用する状態と操作関数
 */
export const useDeliverySettingFilter = ({
  errdialog,
  errMessage,
  displayInputDialog,
  displayConfirmDialog,
  selectShopFlg,
  selectAreaFlg,
  mode,
  model,
  queryDeliverytime,
  onChangeGroup,
  filterState: externalFilterState,
}: UseDeliverySettingFilterParams) => {
  const filterState = externalFilterState ?? useDeliverySettingFilterState();

  const {
    chainSelectValues,
    shopSelectValues,
    areaSelectValues,
    areaEditSelectValues,
    deliveryGroupSelectValues,
    shopListItems,
    areaListItems,
    shopListInput,
    deliveryTimeListInput,
    deliveryTimeCommonInput,
    deliveryTimeDeleteInput,
    deliveryTimeGetInput,
    deliveryGroupListInput,
    deliveryGroupListItems,
    listDeliveryTime,
    listDataArray,
    listGroupDeliveryIdDataArray,
    mappedListData,
  } = filterState;

  /**
   * 「チェーン」選択ボックス @change
   */
  const onChangeChain = async (): Promise<void> => {
    try {
      if (!chainSelectValues.value) {
        // 入力をリセットする際にnullアクセスを避けるためのガード句
        chainSelectValues.value = { chain_code: "", name: "" };
      }

      // リセット
      deliveryTimeListInput.shop_code = "";
      deliveryTimeCommonInput.shop_code = "";
      deliveryTimeDeleteInput.shop_code = "";
      deliveryTimeCommonInput.area_code = "";
      deliveryTimeDeleteInput.area_code = "";
      deliveryGroupSelectValues.value.group_code = "";
      deliveryGroupSelectValues.value.name = "";

      shopSelectValues.value = { shop_code: "", name: "" };
      areaSelectValues.value = { area_code: "", area_name: "" };
      areaEditSelectValues.value = { area_code: "", area_name: "" };
      shopListItems.value = [];
      areaListItems.value = [];
      listDeliveryTime.value = [];
      listDataArray.value = [];
      mappedListData.value = new Map();
      deliveryGroupListItems.value = [];
      listGroupDeliveryIdDataArray.value = [];

      // チェーンが未選択の場合は終了
      if (!chainSelectValues.value.chain_code) {
        return;
      }

      // チェーンコードを各 input に設定
      shopListInput.chain_code = chainSelectValues.value.chain_code;
      deliveryTimeListInput.chain_code = chainSelectValues.value.chain_code;
      deliveryTimeCommonInput.chain_code = chainSelectValues.value.chain_code;
      deliveryTimeDeleteInput.chain_code = chainSelectValues.value.chain_code;
      deliveryTimeGetInput.chain_code = chainSelectValues.value.chain_code;

      // 店舗リスト取得
      shopListItems.value = await getShopList(shopListInput);

      // Apollo クライアント生成
      const apolloClient = await generateApolloClient();

      // エリアデータ取得
      const listArea = await apolloClient.query<ListDeliveryAreaQuery, ListDeliveryAreaQueryVariables>({
        query: ListDeliveryAreaDocument,
        variables: {
          chain_code: chainSelectValues.value.chain_code,
          customer_code: localStorageGetCustomerCode(),
          shop_code: shopSelectValues.value.shop_code,
        },
        fetchPolicy: "no-cache",
      });
      areaListItems.value = listArea.data.listDeliveryArea;

      // 配達グループタブの場合、配達グループリスト取得
      deliveryGroupListInput.chain_code = chainSelectValues.value.chain_code;
      const listDeliveryGroup = await apolloClient.query<ListDeliveryGroupQuery, ListDeliveryGroupQueryVariables>({
        query: ListDeliveryGroupDocument,
        variables: { input: deliveryGroupListInput },
        fetchPolicy: "no-cache",
      });
      deliveryGroupListItems.value = listDeliveryGroup.data.listDeliveryGroup;

    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
    }
  };

  /**
   * 店舗選択ボックス @change
   */
  const onChangeShop = async (): Promise<void> => {
    selectShopFlg.value = true;
    selectAreaFlg.value = false;

    // 店舗未選択時のリセット(エリア名も空にする)
    if (!shopSelectValues.value) {
      areaSelectValues.value = { area_code: "", area_name: "" };
      areaEditSelectValues.value = { area_code: "", area_name: "" };
      areaListItems.value = [];
      listDeliveryTime.value = [];
      listDataArray.value = [];
      mappedListData.value = new Map();
      return;
    }

    const apolloClient = await generateApolloClient();

    // リセット
    areaSelectValues.value = { area_code: "", area_name: "" };
    areaEditSelectValues.value = { area_code: "", area_name: "" };
    areaListItems.value = [];
    listDeliveryTime.value = [];
    listDataArray.value = [];
    mappedListData.value = new Map();

    // shop_code の設定
    deliveryTimeListInput.shop_code = shopSelectValues.value.shop_code;
    deliveryTimeCommonInput.shop_code = shopSelectValues.value.shop_code;
    deliveryTimeDeleteInput.shop_code = shopSelectValues.value.shop_code;
    deliveryTimeCommonInput.area_code = "";
    deliveryTimeDeleteInput.area_code = "";

    delete deliveryTimeListInput["area_code"];

    // エリアデータ取得
    const listArea = await apolloClient.query<ListDeliveryAreaQuery, ListDeliveryAreaQueryVariables>({
      query: ListDeliveryAreaDocument,
      variables: {
        chain_code: chainSelectValues.value.chain_code,
        customer_code: localStorageGetCustomerCode(),
        shop_code: shopSelectValues.value.shop_code,
      },
      fetchPolicy: "no-cache",
    });
    areaListItems.value = listArea.data.listDeliveryArea;

    // 配達時間一覧取得
    await queryDeliverytime();
  };

  /**
   * エリア選択ボックス @change (Composition API 用)
   */
  const onChangeArea = async (): Promise<void> => {
    selectAreaFlg.value = true;
    selectShopFlg.value = false;

    // エリア未選択時のリセット
    if (!areaSelectValues.value) {
      areaEditSelectValues.value = { area_code: "", area_name: "" };
      listDeliveryTime.value = [];
      listDataArray.value = [];
      mappedListData.value = new Map();
      return;
    }

    // リセット
    listDeliveryTime.value = [];
    listDataArray.value = [];

    // area_code の設定
    deliveryTimeListInput.area_code = [areaSelectValues.value.area_code];
    deliveryTimeCommonInput.area_code = areaSelectValues.value.area_code;
    deliveryTimeDeleteInput.area_code = areaSelectValues.value.area_code;

    areaEditSelectValues.value = {
      area_code: areaSelectValues.value.area_code,
      area_name: areaSelectValues.value.area_name,
    };

    // 配達時間一覧取得
    await queryDeliverytime();
  };

  /**
   * 配達グループ設定タブ @Click
   */
  const onClickGroupTab = async (): Promise<void> => {
    mode.value = "group";
    model.value = 1;
    deliveryGroupSelectValues.value = { group_code: "", name: "" };
    areaSelectValues.value = { area_code: "", area_name: "" };
    shopSelectValues.value = { shop_code: "", name: "" };
    listGroupDeliveryIdDataArray.value = [];
    mappedListData.value = new Map();
    listDataArray.value = [];

    // 入力画面 非表示
    displayInputDialog.value = false;

    // 確認画面 非表示
    displayConfirmDialog.value = false;
  };

  /**
   * 配達時間設定タブ @Click
   */
  const onClickTimeTab = async (): Promise<void> => {
    mode.value = "time";
    model.value = 0;
    deliveryGroupSelectValues.value = { group_code: "", name: "" };
    areaSelectValues.value = { area_code: "", area_name: "" };
    shopSelectValues.value = { shop_code: "", name: "" };
    listGroupDeliveryIdDataArray.value = [];
    mappedListData.value = new Map();
    listDataArray.value = [];

    // 入力画面 非表示
    displayInputDialog.value = false;

    // 確認画面 非表示
    displayConfirmDialog.value = false;
  };

  return {
    ...filterState,
    onChangeChain,
    onChangeShop,
    onChangeArea,
    onClickGroupTab,
    onClickTimeTab,
  };
};
